// ═══════════════════════════════════════════════════════════════════════════
//  JELAJAH LUAR — SPOTIFY (kategori → album/playlist → trek, cari) & YOUTUBE
//  (cari + chart). 3 Okt 2026.
//
//  Dipakai DUA layar dengan klien berbeda:
//    · remote tamu (/r)   → klienLuarTamu(kode), aksi: antre / nyanyi lokal
//    · staf (tab Katalog Luar) → klien /api/luar, aksi: daftar pesanan
//
//  Spotify TIDAK diputar di sini maupun di TV — antrean menyimpan id trek dan
//  layar TV membuka aplikasi Spotify di komputer karaoke (keputusan pemilik).
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Search, Loader2, ChevronLeft, Mic2, Youtube, Music2, ListPlus, ExternalLink, Tv } from 'lucide-react'
import api from '../lib/api'
import { tutupKeyboard } from '../lib/keyboard'
import {
  pesanGalat, jam, uriSpotify, refSpotify, buatKlienLuar,
  type KlienLuar, type LaguLuar, type Koleksi, type IsiKoleksi, type StatusLuar,
  type IsiKategori, type HasilCariSpotify, type HasilCariYouTube, type GenreLuar, type StatusGenre,
} from '../lib/tamu'
import { useBahasa } from '../lib/bahasa'

export type AksiLuar = {
  /** Tamu: masukkan item Spotify/YouTube ke antrean room. */
  antre?: (l: LaguLuar) => Promise<void>
  /** Tamu: lagu yang sama ada di katalog kita → antre versi karaoke lokal. */
  nyanyiLokal?: (songId: string, judul: string) => Promise<void>
  /** Staf: masukkan ke daftar pesanan (kr_wantlist). */
  wantlist?: (l: LaguLuar) => Promise<void>
  /** Staf (20 Okt 2026): satu klik jadi entri draft katalog — tinggal isi berkasnya. */
  keKatalog?: (l: LaguLuar) => Promise<void>
  /** Pindah ke YouTube dengan kueri "<judul> <artis>" + karaoke. */
  cariKaraoke?: (q: string) => void
}

const WARNA = [
  'from-rose-500/70 to-orange-500/50', 'from-violet-500/70 to-indigo-500/50',
  'from-emerald-500/70 to-teal-500/50', 'from-sky-500/70 to-blue-600/50',
  'from-amber-400/70 to-yellow-600/50', 'from-pink-500/70 to-fuchsia-600/50',
]

function Gambar({ url, teks, warna, className }: { url?: string; teks: string; warna: string; className?: string }) {
  const [rusak, setRusak] = useState(false)
  return (
    <span className={`relative overflow-hidden bg-gradient-to-br ${warna} grid place-items-center shrink-0 ${className ?? ''}`}>
      {url && !rusak
        ? <img src={url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={() => setRusak(true)} />
        : <span className="font-semibold text-white/85">{teks.split(/\s+/).slice(0, 2).map(x => x[0]?.toUpperCase() ?? '').join('')}</span>}
    </span>
  )
}

function Tombol({ onClick, children, warna = 'netral', title, disabled }: {
  onClick: () => void; children: ReactNode; warna?: 'emas' | 'hijau' | 'merah' | 'netral'; title?: string; disabled?: boolean
}) {
  const w = warna === 'emas' ? 'bg-gold text-black border-gold'
    : warna === 'hijau' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30'
    : warna === 'merah' ? 'bg-red-500/15 text-red-300 border-red-400/30'
    : 'bg-white/5 text-text-2 border-white/10'
  return (
    <button onClick={onClick} title={title} disabled={disabled}
      className={`shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-full border text-[11.5px] font-medium disabled:opacity-40 ${w}`}>
      {children}
    </button>
  )
}

/** Satu baris lagu dengan tombol aksi. */
function BarisLagu({ l, k, aksi, sibuk, setSibuk }: {
  l: LaguLuar; k: number; aksi: AksiLuar; sibuk: string; setSibuk: (s: string) => void
}) {
  const { t } = useBahasa()
  const kunci = `${l.sumber}:${l.id || l.judul}:${k}`
  const jalan = async (f: () => Promise<void>) => {
    if (sibuk) return
    setSibuk(kunci)
    try { await f() } finally { setSibuk('') }
  }
  const sp = l.sumber === 'spotify'
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 flex items-center gap-3 flex-wrap">
      {l.posisi ? <span className="w-6 text-[11px] text-text-3 tabular-nums">{String(l.posisi).padStart(2, '0')}</span> : null}
      <Gambar url={l.sampul} teks={l.artis || l.judul} warna={WARNA[k % WARNA.length]}
        className={`${sp ? 'w-11 h-11' : 'w-16 h-11'} rounded-xl text-[12px]`} />
      {/* 21 Okt: judul & artis punya lebar minimum (tidak terjepit tombol);
          kalau sempit, tombol turun ke baris berikutnya. */}
      <div className="min-w-[180px] flex-1 basis-48">
        <div className="text-[13px] font-medium truncate" title={l.album || l.judul}>{l.judul}</div>
        <div className="text-[11px] text-text-3 truncate" title={l.artis}>
          {l.artis}{l.durasi ? ` · ${jam(l.durasi)}` : ''}
          {l.song_id && <span className="text-gold"> · {t('luar.adaKatalog')}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap justify-end ml-auto">
        {sibuk === kunci && <Loader2 size={15} className="animate-spin text-text-3" />}
        {l.song_id && aksi.nyanyiLokal && (
          <Tombol warna="emas" onClick={() => jalan(() => aksi.nyanyiLokal!(l.song_id!, l.judul))}>
            <Mic2 size={13} /> {t('luar.nyanyiKaraoke')}
          </Tombol>
        )}
        {aksi.antre && (sp || l.id) && (
          <Tombol warna={sp ? 'hijau' : 'merah'} onClick={() => jalan(() => aksi.antre!(l))}
            title={sp ? t('kontrol.spotifyLokal') : undefined}>
            {sp ? <Music2 size={13} /> : <Tv size={13} />} {sp ? t('luar.putarSpotify') : t('luar.putarTv')}
          </Tombol>
        )}
        {!l.song_id && aksi.cariKaraoke && (
          <Tombol onClick={() => aksi.cariKaraoke!(`${l.judul} ${l.artis.split(',')[0]}`.trim())}>
            <Youtube size={13} /> {t('luar.cariVersiKaraoke')}
          </Tombol>
        )}
        {aksi.keKatalog && !l.song_id && (
          <Tombol warna="emas" onClick={() => jalan(() => aksi.keKatalog!(l))}><ListPlus size={13} /> + Katalog</Tombol>
        )}
        {aksi.wantlist && !l.song_id && (
          <Tombol onClick={() => jalan(() => aksi.wantlist!(l))}><ListPlus size={13} /> {t('luar.wantlist')}</Tombol>
        )}
        {!aksi.antre && (
          <a href={sp ? uriSpotify(refSpotify(l)).web : `https://www.youtube.com/watch?v=${l.id}`} target="_blank" rel="noreferrer"
            className="shrink-0 w-8 h-8 rounded-full bg-white/5 border border-white/10 grid place-items-center text-text-3">
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  )
}

function KartuKoleksi({ k, i, buka }: { k: Koleksi; i: number; buka: () => void }) {
  return (
    <button onClick={buka}
      className="text-left rounded-[20px] overflow-hidden border border-white/10 bg-white/[0.05] hover:border-white/25 active:scale-[0.98] transition">
      <div className="relative aspect-square">
        <Gambar url={k.sampul} teks={k.nama} warna={WARNA[i % WARNA.length]}
          className={`absolute inset-0 text-[30px] ${k.jenis === 'artis' ? 'rounded-full m-3' : ''}`} />
        <span className="absolute top-2 left-2 text-[9.5px] uppercase tracking-wider bg-black/60 rounded px-1.5 py-0.5">{k.jenis}</span>
      </div>
      <div className="p-2.5">
        <div className="text-[12.5px] font-medium truncate">{k.nama}</div>
        <div className="text-[10.5px] text-text-3 truncate">{k.subjudul}{k.jumlah ? ` · ${k.jumlah}` : ''}</div>
      </div>
    </button>
  )
}

const Muat = () => {
  const { t } = useBahasa()
  return <div className="text-[12.5px] text-text-3 py-8 text-center"><Loader2 size={16} className="animate-spin inline mr-1" /> {t('jelajah.memuat')}</div>
}

// ── SPOTIFY ────────────────────────────────────────────────────────────────

export function JelajahSpotify({ klien, aksi }: { klien: KlienLuar; aksi: AksiLuar }) {
  const { t } = useBahasa()
  const [status, setStatus] = useState<StatusLuar | null>(null)
  const [kat, setKat] = useState('')
  const [isiKat, setIsiKat] = useState<Record<string, IsiKategori>>({})
  const [buka, setBuka] = useState<Koleksi | null>(null)
  const [isi, setIsi] = useState<IsiKoleksi | null>(null)
  const [q, setQ] = useState('')
  const [hasil, setHasil] = useState<HasilCariSpotify | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')
  const [sibuk, setSibuk] = useState('')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    klien.status().then(s => { setStatus(s); if (s.kategori[0]) setKat(k => k || s.kategori[0].kode) })
      .catch(e => setGalat(pesanGalat(e)))
  }, [klien])

  useEffect(() => {
    if (!kat || isiKat[kat]) return
    let hidup = true
    setMemuat(true); setGalat('')
    klien.kategori(kat).then(d => { if (hidup) setIsiKat(m => ({ ...m, [kat]: d })) })
      .catch(e => { if (hidup) setGalat(pesanGalat(e)) })
      .finally(() => { if (hidup) setMemuat(false) })
    return () => { hidup = false }
  }, [kat, klien, isiKat])

  useEffect(() => {
    if (!buka) { setIsi(null); return }
    let hidup = true
    setMemuat(true); setGalat(''); setIsi(null)
    klien.koleksi(buka.jenis, buka.id).then(d => { if (hidup) setIsi(d) })
      .catch(e => { if (hidup) setGalat(pesanGalat(e)) })
      .finally(() => { if (hidup) setMemuat(false) })
    return () => { hidup = false }
  }, [buka, klien])

  // Pencarian disinggahkan server per kueri; jeda 600 ms supaya tidak
  // menembak Spotify per huruf.
  useEffect(() => {
    window.clearTimeout(timer.current)
    if (q.trim().length < 2) { setHasil(null); return }
    timer.current = window.setTimeout(async () => {
      setMemuat(true); setGalat('')
      try { setHasil(await klien.cariSpotify(q.trim())) } catch (e) { setGalat(pesanGalat(e)) }
      finally { setMemuat(false) }
    }, 600)
    return () => window.clearTimeout(timer.current)
  }, [q, klien])

  const daftarLagu = (lagu: LaguLuar[]) => (
    <div className="grid gap-1.5 xl:grid-cols-2">
      {lagu.map((l, k) => <BarisLagu key={`${l.id}-${k}`} l={l} k={k} aksi={aksi} sibuk={sibuk} setSibuk={setSibuk} />)}
    </div>
  )
  const gridKoleksi = (ks: Koleksi[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
      {ks.map((k, i) => <KartuKoleksi key={k.jenis + k.id} k={k} i={i} buka={() => setBuka(k)} />)}
    </div>
  )

  return (
    <div className="mt-3 space-y-3">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" />
        <input value={q} onChange={e => { setQ(e.target.value); setBuka(null) }} placeholder={t('luar.cariSpotify')}
          enterKeyHint="search"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); tutupKeyboard(e.currentTarget) } }}
          className="w-full !bg-white/5 !border-white/10 !rounded-2xl !pl-10 !pr-9 !py-3 !text-[13.5px]" />
        {memuat && <Loader2 size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-text-3" />}
      </div>
      {status && !status.spotify_kunci && (
        <div className="text-[11px] text-text-3">{t('luar.tanpaKunciSp')}</div>
      )}
      {galat && <div className="text-[12px] text-red-300">{galat}</div>}

      {buka ? (
        <div className="space-y-3">
          <button onClick={() => setBuka(null)} className="inline-flex items-center gap-1 text-[12.5px] text-text-2">
            <ChevronLeft size={16} /> {t('luar.kembali')}
          </button>
          <div className="flex items-center gap-4">
            <Gambar url={isi?.sampul || buka.sampul} teks={buka.nama} warna={WARNA[1]}
              className={`w-24 h-24 text-[26px] ${buka.jenis === 'artis' ? 'rounded-full' : 'rounded-2xl'}`} />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-400">Spotify · {buka.jenis}</div>
              <div className="text-[20px] font-semibold truncate">{isi?.nama || buka.nama}</div>
              <div className="text-[12px] text-text-3 truncate">{isi?.subjudul || buka.subjudul}</div>
            </div>
          </div>
          {memuat && !isi && <Muat />}
          {isi && isi.lagu.length > 0 && daftarLagu(isi.lagu)}
          {isi && isi.album && isi.album.length > 0 && (
            <>
              <div className="text-[13px] font-semibold pt-2">{t('luar.album')}</div>
              {gridKoleksi(isi.album)}
            </>
          )}
          {isi && isi.lagu.length === 0 && (!isi.album || isi.album.length === 0) && (
            <div className="text-[12.5px] text-text-3 py-6 text-center">{t('luar.kosong')}</div>
          )}
        </div>
      ) : q.trim().length >= 2 ? (
        <div className="space-y-4">
          {hasil && hasil.artis.length > 0 && (
            <div>
              <div className="text-[13px] font-semibold mb-2">{t('luar.artis')}</div>
              {gridKoleksi(hasil.artis.slice(0, 6))}
            </div>
          )}
          {hasil && hasil.lagu.length > 0 && (
            <div>
              <div className="text-[13px] font-semibold mb-2">{t('luar.lagu')}</div>
              {daftarLagu(hasil.lagu)}
            </div>
          )}
          {hasil && hasil.album.length > 0 && (
            <div>
              <div className="text-[13px] font-semibold mb-2">{t('luar.album')}</div>
              {gridKoleksi(hasil.album)}
            </div>
          )}
          {hasil && hasil.lagu.length + hasil.album.length + hasil.artis.length === 0 && !memuat && (
            <div className="text-[12.5px] text-text-3 py-6 text-center">{t('luar.tidakAdaHasil')}</div>
          )}
        </div>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {(status?.kategori ?? []).map(k => (
              <button key={k.kode} onClick={() => setKat(k.kode)}
                className={`shrink-0 px-3.5 py-2 rounded-full text-[12.5px] border transition ${
                  kat === k.kode ? 'bg-emerald-500 text-black border-emerald-500 font-semibold' : 'bg-white/[0.06] border-white/10 text-text-2'}`}>
                {k.nama}
              </button>
            ))}
          </div>
          {memuat && !isiKat[kat] && <Muat />}
          {isiKat[kat] && (
            <div className="space-y-4">
              {isiKat[kat].koleksi.length > 0 && gridKoleksi(isiKat[kat].koleksi)}
              {isiKat[kat].lagu.length > 0 && daftarLagu(isiKat[kat].lagu.slice(0, 50))}
              {isiKat[kat].koleksi.length === 0 && isiKat[kat].lagu.length === 0 && (
                <div className="text-[12.5px] text-text-3 py-6 text-center">
                  {t('luar.kosong')}{isiKat[kat].catatan?.length ? ` (${isiKat[kat].catatan![0]})` : ''}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── YOUTUBE ────────────────────────────────────────────────────────────────

export function JelajahYouTube({ klien, aksi, awal }: { klien: KlienLuar; aksi: AksiLuar; awal?: { q: string; n: number } }) {
  const { t } = useBahasa()
  const [q, setQ] = useState('')
  const [karaoke, setKaraoke] = useState(true)
  const [mode, setMode] = useState<'chart' | 'cari'>('chart')
  const [chart, setChart] = useState<LaguLuar[] | null>(null)
  const [hasil, setHasil] = useState<HasilCariYouTube | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')
  const [sibuk, setSibuk] = useState('')

  useEffect(() => {
    klien.chartYouTube().then(setChart).catch(() => setChart([]))
  }, [klien])

  // Kuota YouTube sempit: pencarian HANYA saat ditekan (bukan per ketikan).
  async function cari(kueri: string, kar = karaoke) {
    const k = kueri.trim()
    if (k.length < 2) return
    setMode('cari'); setMemuat(true); setGalat('')
    try { setHasil(await klien.cariYouTube(k, kar)) } catch (e) { setGalat(pesanGalat(e)) }
    finally { setMemuat(false) }
  }

  useEffect(() => {
    if (!awal?.q) return
    setQ(awal.q); setKaraoke(true)
    void cari(awal.q, true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awal?.n])

  const aksiChart: AksiLuar = {
    ...aksi,
    // Entri chart tanpa video id → cari videonya.
    cariKaraoke: (s) => { setQ(s); void cari(s, karaoke) },
  }
  const daftar = mode === 'chart' ? (chart ?? []) : (hasil?.video ?? [])

  return (
    <div className="mt-3 space-y-3">
      <form onSubmit={e => {
        e.preventDefault()
        // 26 Okt: ✓/Cari di keyboard tablet → tutup keyboard juga.
        tutupKeyboard()
        void cari(q)
      }} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-3" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('luar.cariYoutube')} enterKeyHint="search"
            className="w-full !bg-white/5 !border-white/10 !rounded-2xl !pl-10 !pr-3.5 !py-3 !text-[13.5px]" />
        </div>
        <button type="button" onClick={() => setKaraoke(v => !v)}
          className={`h-11 px-3 rounded-2xl border text-[12px] ${karaoke ? 'bg-gold/15 border-gold/40 text-gold' : 'bg-white/5 border-white/10 text-text-3'}`}>
          {karaoke ? '✓ ' : ''}{t('luar.karaoke')}
        </button>
        <button type="submit" className="h-11 px-4 rounded-2xl bg-red-500 text-white text-[13px] font-semibold">
          {memuat ? <Loader2 size={15} className="animate-spin" /> : t('luar.cari')}
        </button>
      </form>
      <div className="flex items-center gap-2 text-[12px]">
        {(['chart', 'cari'] as const).map(m => (
          <button key={m} onClick={() => setMode(m)}
            className={`px-3 py-1.5 rounded-full border ${mode === m ? 'bg-white text-black border-white font-semibold' : 'border-white/10 text-text-2'}`}>
            {m === 'chart' ? t('luar.chart') : t('luar.hasil')}
          </button>
        ))}
        <span className="text-[11px] text-text-3">{t('luar.kuotaCatatan')}</span>
      </div>
      {galat && <div className="text-[12px] text-red-300">{galat}</div>}
      {mode === 'cari' && hasil?.perlu_kunci && <div className="text-[12px] text-amber-300">{t('luar.tanpaKunciYt')}</div>}
      {memuat && mode === 'cari' && !hasil && <Muat />}
      {mode === 'chart' && chart === null && <Muat />}
      {daftar.length === 0 && !memuat && (mode === 'chart' ? chart !== null : hasil !== null) && (
        <div className="text-[12.5px] text-text-3 py-6 text-center">{mode === 'chart' ? t('jelajah.chartKosong') : t('luar.tidakAdaHasil')}</div>
      )}
      <div className="grid gap-1.5 xl:grid-cols-2">
        {daftar.map((l, k) => (
          <BarisLagu key={`${l.id}-${k}`} l={l} k={k} aksi={mode === 'chart' ? aksiChart : { ...aksi, cariKaraoke: undefined }}
            sibuk={sibuk} setSibuk={setSibuk} />
        ))}
      </div>
    </div>
  )
}

// ── STAF: TAB "SPOTIFY & YOUTUBE" (katalog pusat) ──────────────────────────
//
// Jelajah yang sama dengan remote tamu, lewat /api/luar (dijaga token staf).
// Aksinya bukan mengantre, melainkan memasukkan lagu yang belum ada di katalog
// ke Daftar Pesanan (kr_wantlist) — sama dengan yang diisi pemantau chart.
export function KatalogLuarStaf() {
  const klien = useMemo(() => buatKlienLuar(<T,>(jalur: string, params?: Record<string, string>) =>
    api.get<T>(`/api/luar${jalur}`, { params }).then(r => r.data)), [])
  const [tab, setTab] = useState<'sp' | 'yt' | 'genre'>('sp')
  const [kabar, setKabar] = useState('')
  const [kueriYT, setKueriYT] = useState<{ q: string; n: number } | undefined>(undefined)

  useEffect(() => {
    if (!kabar) return
    const t = window.setTimeout(() => setKabar(''), 3500)
    return () => window.clearTimeout(t)
  }, [kabar])

  const wantlist = async (l: LaguLuar) => {
    try {
      const r = await api.post<{ ok: boolean; sudah_katalog?: boolean; status?: string }>('/api/luar/wantlist', {
        judul: l.judul, artis: l.artis.split(',')[0].trim() || l.artis,
        catatan: `Dari ${l.sumber === 'spotify' ? 'Spotify' : 'YouTube'}${l.id ? ` (${l.id})` : ''}`,
      })
      setKabar(r.data.sudah_katalog ? `"${l.judul}" sudah ada di katalog.`
        : `"${l.judul}" masuk Daftar Pesanan (status: ${r.data.status ?? 'baru'}).`)
    } catch (e) { setKabar(pesanGalat(e)) }
  }
  // 20 Okt 2026: "+ Katalog" — entri draft (judul/artis/bahasa/asal) langsung
  // dibuat; berkasnya diisi di Katalog → Siapkan (unggah / impor URL).
  const [bahasaBaru, setBahasaBaru] = useState('id')
  const [sumberBaru, setSumberBaru] = useState('lisensi')
  const keKatalog = async (l: LaguLuar) => {
    try {
      // 21 Okt: chart YouTube (kworb "Top Songs") tidak membawa id video → tautan
      // acuan jatuh ke halaman pencarian YouTube "judul artis" supaya tetap terisi.
      const cari = encodeURIComponent(`${l.judul} ${l.artis.split(',')[0].trim() || l.artis}`)
      const referensi = l.sumber === 'youtube'
        ? (l.id ? `https://www.youtube.com/watch?v=${l.id}` : `https://www.youtube.com/results?search_query=${cari}`)
        : l.sumber === 'spotify' ? uriSpotify(refSpotify(l)).web : ''
      const r = await api.post<{ ok: boolean; song_id: string; lagu_baru: boolean }>('/api/luar/katalog', {
        judul: l.judul, artis: l.artis.split(',')[0].trim() || l.artis, bahasa: bahasaBaru, sumber: sumberBaru,
        referensi_url: referensi,
      })
      setKabar(r.data.lagu_baru ? `"${l.judul}" dibuat di katalog (draft) — isi berkasnya di Katalog → Siapkan.`
        : `"${l.judul}" sudah ada di katalog.`)
    } catch (e) { setKabar(pesanGalat(e)) }
  }
  const aksi: AksiLuar = {
    wantlist, keKatalog,
    cariKaraoke: q => { setKueriYT(k => ({ q, n: (k?.n ?? 0) + 1 })); setTab('yt') },
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        {([['sp', 'Spotify'], ['yt', 'YouTube'], ['genre', 'Status genre']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-[13px] border ${tab === id
              ? (id === 'sp' ? 'bg-emerald-500 text-black border-emerald-500 font-semibold'
                : id === 'yt' ? 'bg-red-500 text-white border-red-500 font-semibold'
                : 'bg-gold text-black border-gold font-semibold')
              : 'bg-white/[0.06] border-white/10 text-text-2'}`}>
            {label}
          </button>
        ))}
        <span className="text-[11.5px] text-text-3 ml-2">
          Lagu yang belum ada di katalog → "+ Katalog" (entri draft) atau "Daftar pesanan". Metadata saja — tidak ada audio yang diunduh.
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11.5px] text-text-3">
        Entri baru: bahasa
        <select value={bahasaBaru} onChange={e => setBahasaBaru(e.target.value)} className="text-[12px]">
          <option value="id">Indonesia</option><option value="en">Inggris</option>
          <option value="zh">Mandarin</option><option value="jp">Jepang</option><option value="kr">Korea</option>
        </select>
        · asal berkas
        <select value={sumberBaru} onChange={e => setSumberBaru(e.target.value)} className="text-[12px]">
          <option value="lisensi">lisensi</option><option value="rekam_ulang">rekam ulang</option>
          <option value="milik_sendiri">milik sendiri</option><option value="publik_domain">publik domain</option>
        </select>
      </div>
      {kabar && <div className="mt-3 text-[12.5px] px-3 py-2 rounded-lg bg-gold/10 border border-gold/30 text-gold">{kabar}</div>}
      {tab === 'sp' && <JelajahSpotify klien={klien} aksi={aksi} />}
      {tab === 'yt' && <JelajahYouTube klien={klien} aksi={{ wantlist, keKatalog }} awal={kueriYT} />}
      {tab === 'genre' && <StatusGenreStaf />}
    </div>
  )
}

// ── STATUS GENRE REMOTE (5 Okt 2026) ───────────────────────────────────────
//
// Status singgahan daftar top per genre (kr_luar_genre) untuk Spotify &
// YouTube, plus pemakaian kuota YouTube hari ini (perkiraan proses backend).
// Genre/override diubah lewat SQL — lihat migrate_katalog_luar.sql bagian 6.
type KuotaYT = {
  unit_hari_ini: number; kuota_harian: number
  cari_tamu_hari_ini: number; cari_tamu_maks: number
  cari_playlist_hari_ini: number; cari_playlist_maks: number
}
const NAMA_BHS: Record<string, string> = { id: 'Indonesia', en: 'Inggris', zh: 'China', ja: 'Jepang', ko: 'Korea' }

function SelStatus({ s }: { s: StatusGenre }) {
  const umur = s.diperbarui ? Math.round((Date.now() - new Date(s.diperbarui).getTime()) / 3600_000) : null
  return (
    <td className="px-2 py-1.5 align-top">
      {s.ada
        ? <span className="text-emerald-300">{s.jumlah} lagu</span>
        : <span className="text-text-3">belum tersedia</span>}
      {umur !== null && <span className="text-text-3"> · {umur} jam lalu</span>}
      {(s.pilih || s.override) && (
        <div className="text-[10.5px] text-text-3 truncate max-w-[260px]"
          title={s.override ? `override: ${s.override}` : `${s.pilih} ${s.pilih_nama ?? ''}`}>
          {s.override ? `override ${s.override}` : `${s.pilih_nama || s.pilih}`}
        </div>
      )}
    </td>
  )
}

function StatusGenreStaf() {
  const [data, setData] = useState<{ genre: GenreLuar[]; kuota_yt?: KuotaYT } | null>(null)
  const [galat, setGalat] = useState('')
  // 30 Okt (Zaldy: "top2nya ko ga ganti, ga latest update"): tarik ulang
  // semua Top & genre sekarang (isi lama tetap dipakai bila sumber gagal).
  const [segar, setSegar] = useState('')
  const [sibukSegar, setSibukSegar] = useState(false)
  const muatGenre = () => api.get<{ genre: GenreLuar[]; kuota_yt?: KuotaYT }>('/api/luar/genre')
    .then(r => setData(r.data)).catch(e => setGalat(pesanGalat(e)))
  useEffect(() => { void muatGenre() }, [])
  const segarkan = async () => {
    setSibukSegar(true); setSegar('')
    try {
      const r = await api.post<{ pesan: string }>('/api/luar/segarkan', {})
      setSegar(r.data.pesan)
      window.setTimeout(() => { void muatGenre() }, 90_000)
    } catch (e) { setSegar(pesanGalat(e)) } finally { setSibukSegar(false) }
  }
  if (galat) return <div className="mt-4 text-[12.5px] text-red-300">{galat}</div>
  if (!data) return <div className="mt-4 text-[12.5px] text-text-3">Memuat…</div>
  const k = data.kuota_yt
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={segarkan} disabled={sibukSegar}
          className="text-[12px] px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 disabled:opacity-50">
          {sibukSegar ? 'Menyegarkan…' : '↻ Segarkan chart sekarang'}
        </button>
        {segar && <span className="text-[12px] text-text-2">{segar}</span>}
      </div>
      {k && (
        <div className="text-[12px] text-text-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
          Kuota YouTube hari ini (perkiraan sejak backend start): <b>{k.unit_hari_ini}</b> / {k.kuota_harian} unit
          · pencarian tamu {k.cari_tamu_hari_ini}/{k.cari_tamu_maks}
          · pencarian playlist genre {k.cari_playlist_hari_ini}/{k.cari_playlist_maks}
        </div>
      )}
      {data.genre.length === 0
        ? <div className="text-[12.5px] text-text-3">Belum ada genre (migrasi kr_luar_genre belum jalan?).</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-text-3 text-left">
                <tr><th className="px-2 py-1">Bahasa</th><th className="px-2 py-1">Genre</th><th className="px-2 py-1">Spotify</th><th className="px-2 py-1">YouTube</th></tr>
              </thead>
              <tbody>
                {data.genre.map(g => (
                  <tr key={`${g.bahasa}:${g.kode}`} className="border-t border-white/[0.06]">
                    <td className="px-2 py-1.5 align-top text-text-2">{NAMA_BHS[g.bahasa] ?? g.bahasa}</td>
                    <td className="px-2 py-1.5 align-top font-medium">{g.nama} <span className="text-text-3 font-normal">({g.kode})</span></td>
                    <SelStatus s={g.sp} />
                    <SelStatus s={g.yt} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  )
}
